// pages/TransportRequest.jsx
// SAP Transport Request (STMS) — MULTI-LANDSCAPE (D01 / D03 / D02).
// Tiap landscape: sub-tab Lintas Landscape + per-sistem (QAS/PRD) + aksi import.
// Backend: /api/transport/{queue,detail,history,import} — semua dengan ?landscape=.
// Permission: 'transport' (lihat), 'transport_import' (tombol Import aktif).
// Struktur data queue = TMSBUFFER (TRKORR/TEXT/OWNER/MAXRC/IMPFLG/BUFPOS).
import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useLang } from '../context/LanguageContext';
import { useAuth, api as axiosApi } from '../context/AuthContext';
import {
  RefreshCw, Truck, Upload, Eye, X, AlertTriangle, CheckCircle2, Clock, History, ChevronUp, ChevronDown, ChevronsUpDown, SlidersHorizontal, FileSpreadsheet, Pencil, ListChecks,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import TrObjectsModal from '../components/TrObjectsModal';
import useIsMobile from '../hooks/useIsMobile';
import useTabDeepLink from '../hooks/useTabDeepLink';

// ── Konfigurasi landscape (SID P01 sama tapi server berbeda per landscape) ────
const LANDSCAPES = [
  { id: 'D01', label: 'ID', route: 'D01 → Q01 → P01', qas: { sid: 'Q01', clients: ['230'] },        prd: { sid: 'P01', clients: ['310'] } },
  { id: 'D03', label: 'JB', route: 'D03 → Q03 → P01', qas: { sid: 'Q03', clients: ['200'] },        prd: { sid: 'P01', clients: ['300'] } },
  { id: 'D02', label: 'KL', route: 'D02 → Q02 → P01', qas: { sid: 'Q02', clients: ['200', '210'] }, prd: { sid: 'P01', clients: ['100'] } },
];
// ── i18n ──────────────────────────────────────────────────────────────────────
// Halaman ini semula seluruhnya Bahasa Indonesia hardcoded. Kamus lokal dipakai
// (bukan context/translations.js) karena file itu ~570KB dan dipakai semua
// halaman; menambah ~50 kunci di sana untuk satu halaman berarti menyunting tiga
// blok bahasa tanpa cara memverifikasi salinan live. Pemilih bahasa tetap jalan.
const STR = {
  en: {
    subtitle: 'STMS import queue — landscape',
    quality: 'Quality', production: 'Production', history: 'History',
    notReleased: 'Not released',
    search: 'Search TR / description / owner…',
    onlyWaiting: 'Not yet imported', onlyNotIn: 'Not yet in', trCount: 'TR',
    cache: 'Cache', refresh: 'Refresh', syncing: 'Syncing…',
    thTr: 'TR', thDesc: 'Description', thOwner: 'Owner', thPos: 'Pos',
    thStatus: 'Status', thAction: 'Action', thType: 'Type', thClient: 'Client',
    thCreated: 'Created', thAge: 'Age', ageUnit: 'd',
    queueEmpty: 'Queue is empty, or nothing matches the filter.',
    queueFail: 'Could not load the queue.',
    noMatch: 'No TR matches.',
    loadingCache: 'Loading cache', syncWithSap: 'Syncing with SAP',
    detailTitle: 'Details & history', descLabel: 'Description:',
    noObjectData: 'No object data',
    histEmpty: 'Cache is empty — click Refresh History to sync from SAP.',
    histNoMatch: 'No history entry matches.',
    ownerUser: 'Owner / User',
    // Tab: not released
    statusOpen: 'Not released', statusReleased: 'Released', statusAll: 'All statuses',
    allDates: 'All dates', last30: 'Last 30 days', last90: 'Last 90 days', last1y: 'Last year',
    allClients: 'All clients', clientNo: 'No client (workbench)', clientPrefix: 'client',
    ownerOpt: 'Owner (optional)',
    pieceList: 'SAP piece lists',
    pieceListTip: 'Piece lists ship with SAP installations and support packages — not requests your team created',
    load: 'Load', reading: 'Reading SAP…',
    loadingConns: 'Loading connections…', noConnProfile: 'No connection profile',
    notReadySuffix: ' — not ready',
    pickThenLoad: 'Select a connection, then click Load.',
    readingE070: 'Reading E070 from SAP…',
    noneOpen: 'No TR is awaiting release.',
    noneReleased: 'No TR has been released.',
    noneMatch: 'No TR matches the filter.',
    of: 'of', loaded: 'loaded', aged90: 'aged 90+ days',
    truncA: 'SAP returned', truncB: 'rows, the same as the maximum',
    truncC: '— so there are more TR that are not shown. Narrow it with the date or owner filter.',
    notReadyA: 'Profile', notReadyB: 'has no RFC_READ_TABLE service path yet. Fill it in under Table Maintenance → SAP Config — as a rule of thumb, if SAP Basis Monitor works for this system the path is correct.',
    source: 'Source', crossClient: 'TR are cross-client, so this list already covers every client in that system.',
    releasedHint: ' · Released; check the Quality/Production tab to see whether it has been imported.',
    plHidden: ' · SAP piece lists are hidden.',
    // Tab TR status (lapis SAP Customizing)
    trStatus: 'TR status',
    tsHint: 'Every TR recorded in the DEV system, released or not — not an import queue. Source: the SAP Customizing config layer.',
    tsSnapshot: 'Snapshot', tsNotSynced: 'The config layer has never been synced for this landscape.',
    tsNotSyncedB: 'Only the ID landscape (D01) is covered so far. This is not an empty result — the data has simply never been collected here.',
    tsNoPerm: 'This view needs the sap_customizing permission.',
    tsFail: 'Could not load the config layer.',
    tsNoConn: 'No DEV connection is available for this landscape.',
    tsNoneMatch: 'No TR matches the filter.',
    tsExcAll: 'Including filtered', tsExcNone: 'Excluding filtered', tsExcOnly: 'Filtered only',
    tsModule: 'Module', tsModApp: 'App component', tsType: 'Type', tsTrStatus: 'Status',
    tsSearch: 'Search TRKORR / description…',
    tsObjects: 'Objects', tsKeys: 'Keys', tsReleased: 'Released', tsExclusion: 'Exclusion',
    tsNotReleased: 'not released', tsUnknown: 'not analysed',
    tsPrev: 'Previous', tsNext: 'Next', tsRows: 'TR', tsShowing: 'showing',
    tsImpDone: 'Imported', tsImpFailed: 'Import failed', tsImpPending: 'Not yet imported',
    tsImpNa: 'Not assessed — the TR has not been released, or it is excluded.',
    tsRowClick: 'Click to see the objects in this TR',
    // Objek TR
    clickForObjects: 'Click to see the objects inside this TR',
    readingTr: 'Reading TR contents from SAP…',
    trEmpty: 'This TR does not contain any objects yet.',
    noConnForObjects: 'No Dev connection profile is ready for this landscape, so TR contents cannot be read. Objects live in the Dev system where the TR was created — not in the target system.',
    request: 'request', task: 'task', objects: 'objects',
    deleted: 'delete',
    emptyTasks: 'Tasks with no objects:',
    emptyTasksHint: 'Empty tasks are a common reason a request cannot be released.',
    reqAttrs: 'Request attributes',
    objTrunc: 'Object list truncated at',
    objTruncB: 'rows — this TR contains more.',
    // Dialog import
    cancel: 'Cancel', loading: 'Loading…',
    noImportHist: 'No import history in', refreshHistory: 'Refresh History',
    mismatchA: 'This profile points at SID', mismatchB: 'but this landscape expects',
    mismatchC: 'Check the Landscape column under Table Maintenance > SAP Config — reading TR from the wrong system is easy to miss.',
    hostBad: 'The host in this profile contains a comma and will almost certainly fail to connect:',
    exUmur: 'Age (days)',
    stoppedNext: ' — remaining clients were not attempted.',
  },
  id: {
    subtitle: 'STMS import queue — landscape',
    quality: 'Quality', production: 'Production', history: 'History',
    notReleased: 'Belum Release',
    search: 'Cari TR / deskripsi / owner…',
    onlyWaiting: 'Hanya belum import', onlyNotIn: 'Hanya belum sampai', trCount: 'TR',
    cache: 'Cache', refresh: 'Refresh', syncing: 'Sinkron…',
    thTr: 'TR', thDesc: 'Deskripsi', thOwner: 'Owner', thPos: 'Pos',
    thStatus: 'Status', thAction: 'Aksi', thType: 'Tipe', thClient: 'Client',
    thCreated: 'Dibuat', thAge: 'Umur', ageUnit: 'hari',
    queueEmpty: 'Queue kosong / tidak ada yang cocok filter.',
    queueFail: 'Gagal memuat queue.',
    noMatch: 'Tidak ada TR yang cocok.',
    loadingCache: 'Memuat cache', syncWithSap: 'Sinkronisasi dengan SAP',
    detailTitle: 'Detail & riwayat', descLabel: 'Deskripsi:',
    noObjectData: 'Tidak ada data objek',
    histEmpty: 'Cache kosong — klik Refresh History untuk sinkron dari SAP.',
    histNoMatch: 'Tidak ada entri history yang cocok.',
    ownerUser: 'Owner / User',
    statusOpen: 'Belum release', statusReleased: 'Sudah release', statusAll: 'Semua status',
    allDates: 'Semua tanggal', last30: '30 hari terakhir', last90: '90 hari terakhir', last1y: '1 tahun terakhir',
    allClients: 'Semua client', clientNo: 'Tanpa client (workbench)', clientPrefix: 'client',
    ownerOpt: 'Owner (opsional)',
    pieceList: 'Piece list SAP',
    pieceListTip: 'Piece list dikirim SAP bersama instalasi & support package — bukan TR buatan tim',
    load: 'Muat', reading: 'Membaca SAP…',
    loadingConns: 'Memuat koneksi…', noConnProfile: 'Tidak ada profil koneksi',
    notReadySuffix: ' — belum siap',
    pickThenLoad: 'Pilih koneksi lalu klik Muat.',
    readingE070: 'Membaca E070 dari SAP…',
    noneOpen: 'Tidak ada TR yang belum di-release.',
    noneReleased: 'Tidak ada TR yang sudah di-release.',
    noneMatch: 'Tidak ada TR yang cocok dengan filter.',
    of: 'dari', loaded: 'dimuat', aged90: 'berumur 90+ hari',
    truncA: 'SAP mengembalikan', truncB: 'baris, sama dengan batas maksimum',
    truncC: '— artinya masih ada TR lain yang belum tampil. Persempit dengan filter tanggal atau owner.',
    notReadyA: 'Profil', notReadyB: 'belum punya service path RFC_READ_TABLE. Isi dulu di Table Maintenance → SAP Config — patokannya, kalau SAP Basis Monitor sudah jalan untuk sistem ini, path-nya sudah benar.',
    source: 'Sumber', crossClient: 'TR bersifat lintas-client, jadi daftar ini sudah mencakup semua client di sistem tersebut.',
    releasedHint: ' · Sudah di-release; cek tab Quality/Production untuk melihat apakah sudah diimport.',
    plHidden: ' · Piece list bawaan SAP disembunyikan.',
    // Tab TR status (lapis SAP Customizing)
    trStatus: 'TR status',
    tsHint: 'Seluruh TR yang tercatat di sistem DEV, dirilis maupun belum — bukan antrean impor. Sumber: lapis config SAP Customizing.',
    tsSnapshot: 'Snapshot', tsNotSynced: 'Lapis config belum pernah disinkronkan untuk landscape ini.',
    tsNotSyncedB: 'Sejauh ini baru landscape ID (D01) yang tercakup. Ini BUKAN hasil kosong — datanya memang belum pernah dikumpulkan di sini.',
    tsNoPerm: 'Tampilan ini butuh izin sap_customizing.',
    tsFail: 'Gagal memuat lapis config.',
    tsNoConn: 'Tidak ada koneksi DEV untuk landscape ini.',
    tsNoneMatch: 'Tidak ada TR yang cocok dengan filter.',
    tsExcAll: 'Termasuk tersaring', tsExcNone: 'Tanpa yang tersaring', tsExcOnly: 'Hanya yang tersaring',
    tsModule: 'Modul', tsModApp: 'App component', tsType: 'Tipe', tsTrStatus: 'Status',
    tsSearch: 'Cari TRKORR / deskripsi…',
    tsObjects: 'Objek', tsKeys: 'Kunci', tsReleased: 'Rilis', tsExclusion: 'Pengecualian',
    tsNotReleased: 'belum dirilis', tsUnknown: 'tidak dianalisis',
    tsPrev: 'Sebelumnya', tsNext: 'Berikutnya', tsRows: 'TR', tsShowing: 'tampil',
    tsImpDone: 'Sudah masuk', tsImpFailed: 'Impor gagal', tsImpPending: 'Belum masuk',
    tsImpNa: 'Tidak dinilai — TR belum dirilis, atau dikecualikan.',
    tsRowClick: 'Klik untuk melihat objek dalam TR ini',
    clickForObjects: 'Klik untuk melihat objek di dalam TR ini',
    readingTr: 'Membaca isi TR dari SAP…',
    trEmpty: 'TR ini belum berisi objek apa pun.',
    noConnForObjects: 'Belum ada profil koneksi Dev yang siap untuk landscape ini, jadi isi TR tidak bisa dibaca. Objek TR tersimpan di sistem Dev tempat TR dibuat — bukan di sistem target.',
    request: 'request', task: 'task', objects: 'objek',
    deleted: 'hapus',
    emptyTasks: 'Task tanpa objek:',
    emptyTasksHint: 'Task kosong sering jadi sebab request tidak bisa di-release.',
    reqAttrs: 'Request attributes',
    objTrunc: 'Daftar objek dipotong pada',
    objTruncB: 'baris — TR ini memuat lebih banyak lagi.',
    cancel: 'Batal', loading: 'Memuat…',
    noImportHist: 'Belum ada riwayat import di', refreshHistory: 'Refresh History',
    mismatchA: 'Profil ini menunjuk SID', mismatchB: 'padahal landscape ini mengharapkan',
    mismatchC: 'Cek kolom Landscape di Table Maintenance > SAP Config — membaca TR dari sistem yang salah sulit disadari.',
    hostBad: 'Host di profil ini mengandung koma dan hampir pasti gagal terhubung:',
    exUmur: 'Umur (hari)',
    stoppedNext: ' — client berikutnya tidak dilanjutkan.',
  },
};
const useT = () => { const { lang } = useLang(); return STR[lang] || STR.en; };

const TAB_ALL = 'ALL';
const TAB_HIST = 'HIST';
const TAB_OPEN = 'OPEN';   // TR belum di-release di Dev
// Tab lapis config (snapshot mingguan), BUKAN antrean STMS. Ditempatkan sebelum
// tab Q01 karena ia menjawab pertanyaan yang lebih luas: seluruh TR di DEV,
// bukan hanya yang menunggu impor.
const TAB_TRST = 'TRST';

// ── Deep link `?tab=` ────────────────────────────────────────────────────────
// Halaman ini bertingkat dua: landscape (`lsId`) lalu view (`tab`). SID saja
// TIDAK cukup sebagai id tab, karena ketiga landscape memakai PRD yang sama
// (`P01`) — `?tab=P01` tidak bisa menentukan landscape mana yang dimaksud.
// Karena itu id tab landscape berbentuk `<lsId>:<sid>`, mis. `D03:P01`.
// `HIST` dan `OPEN` tetap polos: keduanya memuat datanya sendiri lintas
// landscape (lihat penjagaan di `load`), jadi tidak terikat `lsId`.
// Daftar id-nya SENGAJA ditulis ulang di searchRegistry.js, tidak di-import dari
// sini: file itu dilarang meng-import komponen React (anti circular import), dan
// meng-import konstanta dari berkas ini akan menarik seluruh komponennya.
const TR_TAB_SEP = ':';
// Menerjemahkan id tab → { lsId, tab }. Mengembalikan null kalau id tidak sah,
// supaya nilai asing dari URL tidak pernah dipakai mentah.
function parseTrTab(p) {
  if (p === TAB_HIST || p === TAB_OPEN || p === TAB_TRST) return { lsId: null, tab: p };
  const [lid, sid] = String(p || '').split(TR_TAB_SEP);
  const l = LANDSCAPES.find(x => x.id === lid);
  if (!l || (sid !== l.qas.sid && sid !== l.prd.sid)) return null;
  return { lsId: lid, tab: sid };
}

// ── useApi (pola sama seperti SapBasisMonitor) ────────────────────────────────
function useApi() {
  return useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const body   = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = (method === 'get' || method === 'delete')
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) { throw new Error(err.response?.data?.error || err.message || 'API error'); }
  }, []);
}

// Export ke Excel via backend (xlsx-js-style). Mengirim data yang sedang tampil.
async function exportXlsx({ columns, rows, sheetName, filename }) {
  const res = await axiosApi.post('/api/transport/export',
    { columns, rows, sheetName, filename }, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data],
    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}.xlsx`;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

// Tombol export kecil dipakai di semua tab.
function ExportButton({ onClick, disabled }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      onClick={async () => { if (busy) return; setBusy(true); try { await onClick(); } catch (e) { alert('Export gagal: ' + (e.response?.data?.error || e.message)); } setBusy(false); }}
      disabled={disabled || busy}
      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 10, border: '1px solid #16794622', background: '#f0fdf4', color: '#15803d', cursor: (disabled || busy) ? 'not-allowed' : 'pointer', fontSize: 12.5, fontWeight: 600 }}>
      <FileSpreadsheet size={13} /> {busy ? 'Menyiapkan…' : 'Export Excel'}
    </button>
  );
}

// ── Util ──────────────────────────────────────────────────────────────────────
const pick = (row, keys) => { for (const k of keys) { const v = row?.[k] ?? row?.[k.toUpperCase()] ?? row?.[k.toLowerCase()]; if (v !== undefined && v !== null && v !== '') return v; } return ''; };
const fmtD = (d) => { const s = String(d || '').trim(); return /^\d{8}$/.test(s) ? `${s.slice(0,4)}-${s.slice(4,6)}-${s.slice(6,8)}` : (s || '—'); };
const fmtT = (t) => { const s = String(t || '').trim(); return /^\d{6}$/.test(s) ? `${s.slice(0,2)}:${s.slice(2,4)}:${s.slice(4,6)}` : (s || ''); };
// TMSTPALOG.TRTIME = timestamp NUMC14 (yyyymmddhhmmss)
const tsDate = (ts) => { const s = String(ts || '').trim(); return /^\d{14}$/.test(s) ? `${s.slice(0,4)}-${s.slice(4,6)}-${s.slice(6,8)}` : fmtD(s); };
const tsTime = (ts) => { const s = String(ts || '').trim(); return /^\d{14}$/.test(s) ? `${s.slice(8,10)}:${s.slice(10,12)}:${s.slice(12,14)}` : fmtT(s); };

// Normalisasi baris queue dari struktur TMSBUFFER.
function normRow(r) {
  return {
    tr:     String(pick(r, ['trkorr', 'request'])),
    text:   String(pick(r, ['text', 'as4text'])),
    owner:  String(pick(r, ['owner', 'as4user'])),
    bufpos: String(pick(r, ['bufpos'])),
    type:   String(pick(r, ['trfunc', 'trfunction'])),
    impflg: String(pick(r, ['impflg'])),
    rc:     pick(r, ['maxrc', 'imp_rc', 'retcode', 'rc']),
    raw:    r,
  };
}

// 'w' pada IMPFLG = menunggu import; selain itu tampilkan MAXRC.
const isWaiting = (r) => String(r?.impflg || '').toLowerCase().includes('w');

function rcColor(r) {
  if (!r)           return '#94a3b8';
  if (isWaiting(r)) return '#64748b';
  const n = parseInt(r.rc, 10);
  if (isNaN(n))   return '#64748b';
  if (n === 0)    return '#16a34a';
  if (n <= 4)     return '#d97706';
  return '#dc2626';
}
function rcLabel(r) {
  if (!r)           return 'Not in queue';
  if (isWaiting(r)) return 'Waiting';
  const n = parseInt(r.rc, 10);
  if (isNaN(n)) return '—';
  return `RC ${n}`;
}

function Badge({ text, color = '#64748b' }) {
  return <span style={{ background: color + '18', color, border: `1px solid ${color}33`, borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>;
}
function Th({ children, w }) { return <th style={{ background: '#f8fafc', padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', width: w }}>{children}</th>; }

// Header kolom yang bisa diklik utk sort. `sk` = kunci sort di state.
function ThSort({ children, w, col, sort, onSort }) {
  const active = sort.col === col;
  const Icon = !active ? ChevronsUpDown : (sort.dir === 'asc' ? ChevronUp : ChevronDown);
  return (
    <th onClick={() => onSort(col)}
      style={{ background: '#f8fafc', padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: active ? '#0f766e' : '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', width: w, cursor: 'pointer', userSelect: 'none' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{children}<Icon size={12} style={{ opacity: active ? 1 : 0.4 }} /></span>
    </th>
  );
}

// Bandingkan nilai: numerik bila keduanya angka, selain itu string case-insensitive.
function cmp(a, b) {
  const na = parseFloat(a), nb = parseFloat(b);
  const bothNum = !isNaN(na) && !isNaN(nb) && String(a).trim() !== '' && String(b).trim() !== '';
  if (bothNum) return na - nb;
  return String(a ?? '').toLowerCase().localeCompare(String(b ?? '').toLowerCase());
}
function useSort(initialCol, initialDir = 'asc') {
  const [sort, setSort] = useState({ col: initialCol, dir: initialDir });
  const onSort = useCallback((col) => {
    setSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  }, []);
  const apply = useCallback((rows, getVal) => {
    if (!sort.col) return rows;
    const arr = [...rows];
    arr.sort((x, y) => { const r = cmp(getVal(x, sort.col), getVal(y, sort.col)); return sort.dir === 'asc' ? r : -r; });
    return arr;
  }, [sort]);
  return { sort, onSort, apply };
}
function Td({ children, title, mono }) { return <td title={title} style={{ padding: '7px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#1e293b', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: mono ? 'monospace' : 'inherit' }}>{children ?? '—'}</td>; }

// ── Dialog konfirmasi import ──────────────────────────────────────────────────
// clients: array — kalau lebih dari satu (mis. Q02: 200 & 210), import berurutan ke SEMUA.
function ImportDialog({ row, landscape, system, clients, isPrd, alreadyImported, onClose, onDone, api }) {
  const L = useT();
  const [typed, setTyped]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');
  const [progress, setProg]   = useState('');
  const [again, setAgain]     = useState(!!alreadyImported); // Import Again utk TR yg sudah pernah diimport
  const multi = clients.length > 1;
  const [selected, setSelected] = useState(clients); // multi-client (e.g. Q02: 200 & 210) — choose which clients to import
  const canGo = !busy && selected.length > 0 && (!isPrd || typed.trim().toUpperCase() === row.tr.toUpperCase());

  const sleep = (ms) => new Promise(res => setTimeout(res, ms));
  const go = async () => {
    setBusy(true); setErr('');
    const results = [];
    const targets = clients.filter(c => selected.includes(c));
    // SAP runs one tp import per target system at a time. When the previous client's
    // background job still holds the lock (CREATE_ENQUEUE_FAILED), wait and retry.
    const MAX_TRY = 10, WAIT_MS = 20000;
    for (const cli of targets) {
      let done = false;
      for (let attempt = 1; attempt <= MAX_TRY && !done; attempt++) {
        setProg('Importing to ' + system + ' client ' + cli + '…' + (attempt > 1 ? ' (retry ' + (attempt - 1) + '/' + (MAX_TRY - 1) + ', waiting for the previous import to finish)' : ''));
        let r;
        try {
          r = await api('/api/transport/import', {
            method: 'POST',
            body: JSON.stringify({ landscape, tr: row.tr, system, client: cli, again }),
          });
        } catch (e) {
          results.push({ client: cli, ok: false, error: e.message });
          setErr('Client ' + cli + ': ' + e.message + (results.length < targets.length ? ' — remaining clients were not imported.' : ''));
          setBusy(false);
          return;
        }
        const res = (r && r.result) || {};
        if (res.success === false) {
          const code = String(res.error || '').trim();
          if (code === 'CREATE_ENQUEUE_FAILED' && attempt < MAX_TRY) { await sleep(WAIT_MS); continue; }
          const msg = [code, res.msg].filter(Boolean).join(' — ') || 'SAP returned success:false';
          results.push({ client: cli, ok: false, error: msg, result: res });
          setErr('Client ' + cli + ': ' + msg + (results.length < targets.length ? ' — remaining clients were not imported.' : ''));
          setBusy(false);
          return;
        }
        results.push({ client: cli, ok: true, result: res });
        done = true;
      }
    }
    onDone({ tr: row.tr, system, results });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 20, width: 420, maxWidth: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Upload size={18} color={isPrd ? '#dc2626' : '#0f766e'} />
          <div style={{ fontWeight: 700, fontSize: 15 }}>Import ke {system} ({landscape})</div>
          <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
          <div><b>TR:</b> <span style={{ fontFamily: 'monospace' }}>{row.tr}</span></div>
          <div><b>{L.descLabel}</b> {row.text || '—'}</div>
          <div style={{ marginTop: 6 }}>
            <b>Client:</b>{' '}
            {multi ? (
              <span style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', verticalAlign: 'middle' }}>
                {clients.map(c => (
                  <label key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'monospace', cursor: busy ? 'default' : 'pointer' }}>
                    <input type="checkbox" disabled={busy} checked={selected.includes(c)}
                      onChange={e => setSelected(prev => e.target.checked ? clients.filter(x => x === c || prev.includes(x)) : prev.filter(x => x !== c))} />
                    {c}
                  </label>
                ))}
              </span>
            ) : <span style={{ fontFamily: 'monospace' }}>{clients.join(' , ')}</span>}
            {multi && <div style={{ color: '#b45309', fontSize: 12 }}>Selected clients are imported one after another; each waits until the previous import job releases the lock.</div>}
          </div>
          {alreadyImported && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12.5, color: '#b45309', cursor: 'pointer' }}>
              <input type="checkbox" checked={again} onChange={e => setAgain(e.target.checked)} />
              Import Again — TR ini sudah pernah diimport ke {system}; centang untuk import ulang (tanpa ini SAP akan melewatinya)
            </label>
          )}
        </div>
        {isPrd && (
          <div style={{ marginTop: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 10, fontSize: 12.5, color: '#991b1b' }}>
            <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
            Target <b>PRODUCTION</b>. Ketik ulang nomor TR untuk konfirmasi:
            <input value={typed} onChange={e => setTyped(e.target.value)} placeholder={row.tr}
              style={{ display: 'block', width: '100%', marginTop: 6, padding: '6px 8px', border: '1px solid #fca5a5', borderRadius: 8, fontFamily: 'monospace' }} />
          </div>
        )}
        {busy && progress && <div style={{ marginTop: 10, fontSize: 12.5, color: '#0f766e' }}>{progress}</div>}
        {err && <div style={{ marginTop: 10, fontSize: 12.5, color: '#dc2626' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy} style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>{L.cancel}</button>
          <button onClick={go} disabled={!canGo} style={{ padding: '8px 14px', borderRadius: 10, border: 'none', background: canGo ? (isPrd ? '#dc2626' : '#0f766e') : '#94a3b8', color: '#fff', cursor: canGo ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
            {busy ? 'Mengirim…' : `Import ke ${system}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Riwayat import (ALOG via action=history) ──────────────────────────────────
function HistoryBlock({ tr, landscape, system, api }) {
  const L = useT();
  const [rows, setRows] = useState(null);
  const [err, setErr]   = useState('');
  useEffect(() => {
    let ok = true;
    api(`/api/transport/history?landscape=${landscape}&tr=${encodeURIComponent(tr)}&system=${system}&live=1`)
      .then(r => {
        if (!ok) return;
        const raw = r?.data?.HISTORY || r?.data?.history || [];
        setRows(Array.isArray(raw) ? raw : []);
      })
      .catch(e => { if (ok) { setErr(e.message); setRows([]); } });
    return () => { ok = false; };
  }, [tr, landscape, system, api]);

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>
        <History size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Riwayat import — {system}
      </div>
      {err && <div style={{ fontSize: 12.5, color: '#dc2626' }}>{err}</div>}
      {!err && rows === null && <div style={{ fontSize: 12.5, color: '#64748b' }}>{L.loading}</div>}
      {rows !== null && !err && (
        rows.length ? (
          <div style={{ border: '1px solid #f1f5f9', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><Th>Tanggal</Th><Th>Jam</Th><Th>Step</Th><Th>RC</Th><Th>User</Th></tr></thead>
              <tbody>
                {rows.map((h, i) => {
                  const rc = parseInt(pick(h, ['retcode', 'rc']), 10);
                  const ts = String(pick(h, ['trtime']));
                  return (
                    <tr key={i}>
                      <Td>{tsDate(ts)}</Td>
                      <Td>{tsTime(ts)}</Td>
                      <Td mono>{String(pick(h, ['trstep', 'step'])) || '—'}</Td>
                      <Td><Badge text={isNaN(rc) ? '—' : `RC ${rc}`} color={isNaN(rc) ? '#64748b' : rc === 0 ? '#16a34a' : rc <= 4 ? '#d97706' : '#dc2626'} /></Td>
                      <Td>{String(pick(h, ['admin', 'truser', 'user'])) || '—'}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <div style={{ fontSize: 12.5, color: '#94a3b8' }}>{L.noImportHist} {system}.</div>
      )}
    </div>
  );
}

// ── Koneksi Dev + isi TR, dipakai bersama oleh tab antrean dan tab Belum Release ──
// Label koneksi dibentuk dari LANDSCAPES di file ini, BUKAN dari kolom
// `landscape` di sap_connections — kolom itu pernah tidak sinkron (baris
// sid=D02 berlabel 'JB', padahal JB adalah D03). SID yang menentukan.
// Profil dipilih memakai kolom `landscape` + `environment` di SAP Config —
// cara profil itu memang dikelola. Label tab (ID/JB/KL) dicocokkan langsung
// dengan kolom landscape.
//
// `expectedSid` dipakai HANYA untuk memperingatkan: bila profil landscape JB
// ternyata menunjuk SID selain D03, itu tetap dipakai (data SAP Config yang
// berkuasa), tapi ketidakcocokannya ditampilkan. Membaca TR dari sistem yang
// salah tanpa sadar jauh lebih berbahaya daripada dropdown yang kosong.
function useDevConns(api, expectedSid, lsLabel) {
  const [conns, setConns] = useState(null);
  useEffect(() => {
    let alive = true;
    setConns(null);
    api(`/api/transport/open/connections?landscape=${encodeURIComponent(lsLabel)}&environment=DEV`)
      .then(list => {
        if (!alive) return;
        setConns((list || []).map(c => ({
          ...c,
          mismatch: !!c.sid && c.sid.toUpperCase() !== expectedSid.toUpperCase(),
          label: `${c.sid || '?'}${c.client ? ' · ' + STR.en.clientPrefix + ' ' + c.client : ''} (${lsLabel})`,
        })));
      })
      .catch(() => alive && setConns([]));
    return () => { alive = false; };
  }, [api, expectedSid, lsLabel]);
  return conns;
}

// Cache isi TR. Dibaca hanya saat sebuah baris dibentangkan — satu TR bisa
// memuat ribuan objek dan sebagian besar baris tidak akan pernah dibuka.
function useTrObjects(api, connId) {
  const [openTr, setOpenTr] = useState(null);
  const [cache, setCache]   = useState({});
  useEffect(() => { setOpenTr(null); setCache({}); }, [connId]);
  const toggle = useCallback(async (tr) => {
    if (openTr === tr) { setOpenTr(null); return; }
    setOpenTr(tr);
    if (!connId || cache[tr]?.data || cache[tr]?.loading) return;
    setCache(c => ({ ...c, [tr]: { loading: true } }));
    try {
      const d = await api(`/api/transport/open/objects?connectionId=${encodeURIComponent(connId)}&trkorr=${encodeURIComponent(tr)}`);
      setCache(c => ({ ...c, [tr]: { data: d } }));
    } catch (e) {
      setCache(c => ({ ...c, [tr]: { err: e.message } }));
    }
  }, [api, connId, openTr, cache]);
  return { openTr, cache, toggle };
}

// Sel TR yang bisa diklik untuk membentangkan isinya.
function TrCell({ tr, open, onClick }) {
  const L = useT();
  return (
    <span onClick={(e) => { e.stopPropagation(); onClick(tr); }}
      title={L.clickForObjects}
      style={{ cursor: 'pointer', userSelect: 'none' }}>
      <span style={{ color: '#0f766e', marginRight: 5, display: 'inline-block',
                     transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>›</span>
      {tr}
    </span>
  );
}

// ── Isi sebuah TR: task → objek, seperti pohon di SE01 ───────────────────────
// Objek tercatat pada TASK, bukan pada request. Karena itu tampilannya
// dikelompokkan per task — struktur yang sama dengan yang dilihat Basis di SE01,
// supaya apa yang tampil di sini bisa dicocokkan langsung dengan SAP.
function TrObjects({ state, noConn }) {
  const L = useT();
  const box = { padding: '12px 16px 14px 34px', fontSize: 12.5 };
  if (noConn)
    return <div style={{ ...box, color: '#92400e' }}>
      {L.noConnForObjects}
    </div>;
  if (!state || state.loading)
    return <div style={{ ...box, color: '#94a3b8' }}>{L.readingTr}</div>;
  if (state.err)
    return <div style={{ ...box, color: '#991b1b' }}>{state.err}</div>;

  const d = state.data || {};
  const tasks = d.tasks || [];
  const empty = d.empty_tasks || [];
  const attrs = d.attributes || [];

  if (!tasks.length && !empty.length && !attrs.length)
    return <div style={{ ...box, color: '#94a3b8' }}>{L.trEmpty}</div>;

  return (
    <div style={box}>
      {tasks.map(t => (
        <div key={t.trkorr} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f766e' }}>{t.trkorr}</span>
            <span style={{ fontSize: 11, color: '#64748b' }}>
              {t.is_request ? L.request : L.task}{t.owner ? ` · ${t.owner}` : ''}
              {t.status ? ` · ${t.status}` : ''}
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>{t.objects.length} {L.objects}</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {t.objects.map((o, i) => (
                <tr key={`${o.pgmid}${o.object}${o.name}${i}`}>
                  <td style={{ padding: '3px 10px 3px 0', width: 210, color: '#475569', verticalAlign: 'top' }}
                      title={`${o.pgmid} ${o.object}`}>
                    {o.type_label}
                  </td>
                  <td style={{ padding: '3px 10px 3px 0', fontFamily: 'monospace', color: '#0f172a', wordBreak: 'break-all' }}>
                    {o.name}
                  </td>
                  <td style={{ padding: '3px 0', width: 70, fontSize: 11, color: '#94a3b8', verticalAlign: 'top' }}>
                    {/* OBJFUNC 'D' = objek DIHAPUS oleh TR ini. Penting: transport
                        yang menghapus objek berperilaku sangat berbeda saat import. */}
                    {o.func === 'D' ? <span style={{ color: '#b91c1c' }}>{L.deleted}</span> : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {empty.length > 0 && (
        <div style={{ marginTop: 4, marginBottom: 10, color: '#92400e' }}>
          {L.emptyTasks} {empty.map(t => `${t.trkorr}${t.owner ? ` (${t.owner})` : ''}`).join(', ')}
          <div style={{ fontSize: 11, color: '#a16207', marginTop: 2 }}>
            {L.emptyTasksHint}
          </div>
        </div>
      )}

      {attrs.length > 0 && (
        <div style={{ marginTop: 4, paddingTop: 8, borderTop: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>{L.reqAttrs}</div>
          {attrs.map((a, i) => (
            <div key={i} style={{ fontFamily: 'monospace', color: '#475569' }}>
              {a.attribute}{a.reference ? ` = ${a.reference}` : ''}
            </div>
          ))}
        </div>
      )}

      {d.truncated && (
        <div style={{ marginTop: 8, color: '#92400e' }}>
          {L.objTrunc} {d.total_objects} {L.objTruncB}
        </div>
      )}
    </div>
  );
}

// ── View: TR yang BELUM di-release di Dev ─────────────────────────────────────
// Sumber datanya BEDA dari tab lain. Tab Quality/Production/History membaca STMS
// import queue lewat ICF ZCL_TRANSPORT_HTTP — isinya TR yang sudah di-release.
// TR yang belum release tidak pernah masuk queue, jadi view ini membaca tabel
// E070/E07T langsung via RFC_READ_TABLE (endpoint /api/transport/open).
//
// Nilai utama daftar ini adalah menemukan TR TERBENGKALAI — yang dibuat lalu
// dilupakan. Karena itu umur diurutkan menurun secara default dan diberi warna,
// bukan sekadar ditampilkan.

// Bentuk nilai import DITENTUKAN backend (cfgImportOf di sapCustomizing.js):
//   { status: 'done' | 'failed' | 'pending', at, rc, via }
// dan `null` bila TR itu tidak pantas dinilai sama sekali (belum dirilis, atau
// dikecualikan) — bukan objek kosong.
//
// ⚠ TIDAK ADA properti boolean `imported`. Versi pertama sel ini mengujinya, dan
// karena undefined selalu falsy, SETIAP baris yang bisa dinilai tampil "belum
// sampai" — termasuk TR yang jelas sudah masuk Q01. Tidak ada error, tidak ada
// sel kosong; hanya kolom yang seluruhnya salah dan terlihat masuk akal. Uji
// `status`, jangan pernah menebak nama properti boolean.
//
// null → titik abu, BUKAN "belum sampai": "tidak dinilai" dan "belum masuk"
// adalah dua pernyataan berbeda, dan menyamakannya menuduh proses transport atas
// TR yang memang belum pernah dikirim.
function TsImportCell({ v, sid, trkorr, L }) {
  if (!v) return <span style={{ color: '#e2e8f0' }} title={L.tsImpNa}>·</span>;
  const M = {
    done:    { ch: '✓', color: '#0d9488', lbl: L.tsImpDone },
    failed:  { ch: '!', color: '#dc2626', lbl: L.tsImpFailed },
    pending: { ch: '—', color: '#b45309', lbl: L.tsImpPending },
  }[v.status] || { ch: '?', color: '#94a3b8', lbl: L.tsImpPending };
  const bits = [M.lbl, sid];
  if (v.at) bits.push(v.at);
  if (v.rc) bits.push(`rc=${v.rc}`);
  // `via` hanya bila catatan ditemukan lewat nomor TASK, bukan nomor TR sendiri.
  if (v.via && v.via !== trkorr) bits.push(`via ${v.via}`);
  return <span title={bits.join(' · ')} style={{ color: M.color, fontWeight: 700, cursor: 'help' }}>{M.ch}</span>;
}

// ── Tab "TR status" — lapis config SAP Customizing ────────────────────────────
// Membaca endpoint yang SAMA dengan halaman SAP Customizing
// (/api/sap/customizing/config?level=tr), bukan antrean STMS. Perbedaannya
// mendasar dan harus terbaca oleh pengguna, bukan hanya oleh pembaca kode:
//
//   tab Q01/P01 : antrean impor LIVE — "TR mana yang menunggu masuk"
//   tab ini     : SELURUH TR di sistem DEV, dirilis maupun belum, dari snapshot
//                 cron mingguan (Sabtu 01.00 WIB)
//
// Karena itu umur snapshot SELALU ditampilkan. Menempatkan tabel bersnapshot
// mingguan bersebelahan dengan antrean live tanpa penanda akan membuat orang
// mengira keduanya sama barunya, dan itu kekeliruan yang tidak melaporkan diri.
//
// ⚠ sap_config_tr baru terisi untuk SATU koneksi (D01 / landscape ID, 10.492
// baris per 21-08). Untuk JB dan KL hasilnya nol baris, dan nol baris di sini
// BUKAN "tidak ada TR" melainkan "belum pernah disync". Keduanya dibedakan
// secara eksplisit di bawah — sama seperti dd_text_ready di tab objek.
function TrStatusView({ api, connId, lsLabel }) {
  const L = useT();
  // TR yang barisnya diklik. null = modal tertutup.
  const [modalTr, setModalTr] = useState(null);
  const [rows, setRows]   = useState(null);
  const [total, setTotal] = useState(0);
  const [snap, setSnap]   = useState('');
  const [tgts, setTgts]   = useState([]);
  const [err, setErr]     = useState('');
  const [loading, setLoad] = useState(false);
  const [q, setQ]         = useState('');
  const [module, setModule] = useState('');
  const [modApp, setModApp] = useState('');
  const [trf, setTrf]       = useState('');
  const [trs, setTrs]       = useState('');
  // Default 'none' menyamakan tampilan awal dengan halaman SAP Customizing.
  // Kalau dibiarkan 'all', cacah barisnya (10.492) akan berbeda dari halaman itu
  // (7.911) tanpa sebab yang terlihat, dan selisih 2.581 TR tersaring akan
  // terbaca sebagai data yang tidak konsisten antar halaman.
  const [exc, setExc]     = useState('none');
  const [sort, setSort]   = useState({ col: 'released_on', dir: 'desc' });
  const [offset, setOffset] = useState(0);
  const LIMIT = 200;

  // Filter apa pun mengembalikan paging ke halaman pertama. Tanpa ini, mengetik
  // filter saat berada di offset 400 memberi hasil kosong yang terlihat seperti
  // "tidak ada yang cocok" padahal cocoknya ada di halaman pertama.
  useEffect(() => { setOffset(0); }, [q, module, modApp, trf, trs, exc, connId]);

  const load = useCallback(async () => {
    if (!connId) { setRows([]); setTotal(0); return; }
    setLoad(true); setErr('');
    try {
      const p = new URLSearchParams({
        connectionId: String(connId), level: 'tr', excluded: exc,
        sort: sort.col, dir: sort.dir, limit: String(LIMIT), offset: String(offset),
      });
      if (q.trim())      p.set('q', q.trim());
      if (module.trim()) p.set('module', module.trim().toUpperCase());
      if (modApp.trim()) p.set('module_app', modApp.trim().toUpperCase());
      if (trf.trim())    p.set('trfunction', trf.trim().toUpperCase());
      if (trs.trim())    p.set('trstatus', trs.trim().toUpperCase());
      const d = await api(`/api/sap/customizing/config?${p.toString()}`);
      setRows(d.rows || []);
      setTotal(d.total || 0);
      setTgts(d.import_targets || []);
      setSnap((d.rows || [])[0]?.snapshot_at || '');
    } catch (e) {
      // 403 dibedakan: izin yang kurang bukan kegagalan muat, dan menyarankan
      // sync ulang untuk masalah izin akan menyesatkan sepenuhnya.
      setErr(/403|ditolak|denied/i.test(e.message) ? L.tsNoPerm : (e.message || L.tsFail));
      setRows([]); setTotal(0);
    }
    setLoad(false);
  }, [api, connId, q, module, modApp, trf, trs, exc, sort, offset, L]);

  useEffect(() => { load(); }, [load]);

  const onSort = (col) =>
    setSort(sv => sv.col === col ? { col, dir: sv.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });

  const SortTh = ({ label, on, w }) => (
    <th onClick={() => onSort(on)} title={label}
      style={{ background: '#f8fafc', padding: '8px 12px', textAlign: 'left', fontWeight: 600,
               fontSize: 11, color: sort.col === on ? '#0f766e' : '#64748b',
               borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase',
               letterSpacing: '0.04em', cursor: 'pointer', userSelect: 'none', width: w }}>
      {label}{sort.col === on ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
    </th>
  );

  const inp = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13 };
  // Filter apa pun sedang aktif? Menentukan apakah nol baris berarti "tidak ada
  // yang cocok" atau "belum pernah disync" — dua sebab yang menuntun ke tindakan
  // yang sama sekali berbeda.
  const anyFilter = !!(q.trim() || module.trim() || modApp.trim() || trf.trim() || trs.trim()) || exc === 'only';
  const neverSynced = rows !== null && !loading && !err && total === 0 && !anyFilter;

  return (
    <div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
        {L.tsHint}
        {snap && <> · <b>{L.tsSnapshot}</b> {String(snap).slice(0, 16).replace('T', ' ')} UTC</>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={L.tsSearch}
          style={{ ...inp, flex: '1 1 240px' }} />
        <input value={module} onChange={e => setModule(e.target.value)} placeholder={L.tsModule}
          style={{ ...inp, width: 120 }} />
        <input value={modApp} onChange={e => setModApp(e.target.value)} placeholder={L.tsModApp}
          style={{ ...inp, width: 140 }} />
        <input value={trf} onChange={e => setTrf(e.target.value)} placeholder={L.tsType}
          style={{ ...inp, width: 90 }} />
        <input value={trs} onChange={e => setTrs(e.target.value)} placeholder={L.tsTrStatus}
          style={{ ...inp, width: 90 }} />
        <select value={exc} onChange={e => setExc(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
          <option value="none">{L.tsExcNone}</option>
          <option value="all">{L.tsExcAll}</option>
          <option value="only">{L.tsExcOnly}</option>
        </select>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>
          {total.toLocaleString()} {L.tsRows}
          {total > LIMIT && <> · {L.tsShowing} {offset + 1}–{Math.min(offset + LIMIT, total)}</>}
        </div>
      </div>

      {err && (
        <div style={{ marginBottom: 12, padding: 10, background: '#fef2f2', border: '1px solid #fecaca',
                      borderRadius: 10, fontSize: 13, color: '#991b1b' }}>{err}</div>
      )}

      {!connId && (
        <div style={{ marginBottom: 12, padding: 10, background: '#fffbeb', border: '1px solid #fde68a',
                      borderRadius: 10, fontSize: 13, color: '#92400e' }}>{L.tsNoConn}</div>
      )}

      {neverSynced && (
        <div style={{ marginBottom: 12, padding: 12, background: '#fffbeb', border: '1px solid #fde68a',
                      borderRadius: 10, fontSize: 13, color: '#92400e' }}>
          <b>{L.tsNotSynced}</b> ({lsLabel}) — {L.tsNotSyncedB}
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="TRKORR" on="trkorr" />
              <SortTh label={L.tsType} on="trfunction" />
              <SortTh label={L.tsTrStatus} on="trstatus" />
              <SortTh label={L.tsModule} on="module" />
              <SortTh label={L.tsModApp} on="module_app" />
              <SortTh label={L.thDesc} on="descr" />
              <SortTh label={L.tsObjects} on="obj_count" />
              <SortTh label={L.tsKeys} on="key_count" />
              <SortTh label={L.tsReleased} on="released_on" />
              {/* Status impor dilekatkan di JS setelah query (task_trkorr adalah
                  daftar dipisah koma yang tidak bisa dipecah di SQLite tanpa trik
                  rapuh), jadi backend tidak punya kolom untuk diurutkan dan kolom
                  ini TIDAK diberi panah. */}
              {tgts.map(tg => <Th key={tg.sid}>{tg.sid}</Th>)}
              <Th>{L.tsExclusion}</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><Td>—</Td><Td>{L.loadingCache}</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td></tr>}
            {!loading && rows !== null && !rows.length && !neverSynced && (
              <tr><Td>—</Td><Td>{L.tsNoneMatch}</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td></tr>
            )}
            {!loading && (rows || []).map(r => (
              <tr key={r.trkorr} onClick={() => setModalTr(r)} title={L.tsRowClick}
                style={{ cursor: 'pointer' }}>
                <Td mono>{r.trkorr}</Td>
                <Td title={`TRFUNCTION = ${r.trfunction}`}>{r.trfunction || '—'}</Td>
                <Td title={`TRSTATUS = ${r.trstatus}`}>{r.trstatus || '—'}</Td>
                <Td title={r.module_src ? `${L.tsModule}: ${r.module_src}` : undefined}>{r.module || '—'}</Td>
                <Td title={r.module_app_all || undefined}>{r.module_app || '—'}</Td>
                <Td title={r.descr}>{r.descr || '—'}</Td>
                {/* NULL ≠ 0. NULL berarti E071 tidak pernah dibaca untuk TR ini
                    (TR tersaring), dan menulis 0 di sana adalah pernyataan palsu:
                    "tidak ada objek berubah" padahal isinya tidak pernah dilihat. */}
                <Td title={r.obj_count == null ? L.tsUnknown : undefined}>
                  {r.obj_count == null ? <span style={{ color: '#cbd5e1' }}>?</span> : r.obj_count}
                </Td>
                <Td title={r.key_count == null ? L.tsUnknown : undefined}>
                  {r.key_count == null ? <span style={{ color: '#cbd5e1' }}>?</span> : r.key_count}
                </Td>
                <Td mono>
                  {fmtSapDate(r.released_on)
                    || <span style={{ color: '#cbd5e1', fontStyle: 'italic', fontFamily: 'inherit' }}>
                         {String(r.trstatus || '').toUpperCase() === 'D' ? L.tsNotReleased : '—'}
                       </span>}
                </Td>
                {tgts.map(tg => (
                  <Td key={tg.sid}>
                    <TsImportCell v={r.imports ? r.imports[tg.sid] : null} sid={tg.sid} trkorr={r.trkorr} L={L} />
                  </Td>
                ))}
                <Td title={r.excluded_reason || undefined}>
                  {r.excluded_reason
                    ? <span style={{ fontSize: 11.5, color: '#b45309' }}>{r.excluded_reason}</span>
                    : <span style={{ color: '#cbd5e1' }}>—</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalTr && (
        <TrObjectsModal api={api} connectionId={connId} tr={modalTr} onClose={() => setModalTr(null)} />
      )}

      {total > LIMIT && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
          <button disabled={offset === 0 || loading} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}
            style={{ padding: '7px 14px', borderRadius: 10, border: '1px solid #e2e8f0',
                     background: '#fff', fontSize: 13, cursor: offset === 0 ? 'default' : 'pointer',
                     opacity: offset === 0 ? 0.5 : 1 }}>{L.tsPrev}</button>
          <button disabled={offset + LIMIT >= total || loading} onClick={() => setOffset(o => o + LIMIT)}
            style={{ padding: '7px 14px', borderRadius: 10, border: '1px solid #e2e8f0',
                     background: '#fff', fontSize: 13,
                     cursor: offset + LIMIT >= total ? 'default' : 'pointer',
                     opacity: offset + LIMIT >= total ? 0.5 : 1 }}>{L.tsNext}</button>
        </div>
      )}
    </div>
  );
}

// E070.AS4DATE datang sebagai 'YYYYMMDD' tanpa pemisah, dan sering juga '' atau
// '00000000' untuk TR yang belum dirilis. Ketiganya jatuh ke keadaan "tidak ada
// tanggal": '00000000' yang lolos akan tampil sebagai tanggal yang terlihat sah.
// Sengaja TIDAK memakai new Date() — string ini tanpa zona waktu, dan mem-parse
// lalu memformat ulang bisa menggeser satu hari tergantung zona peramban.
function fmtSapDate(v) {
  const s = String(v || '').trim();
  if (!/^\d{8}$/.test(s) || s === '00000000') return '';
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

function OpenTRView({ q, api, conns, expectedSid }) {
  const L = useT();
  const [connId, setConnId] = useState('');
  const [rows, setRows]     = useState(null);
  const [meta, setMeta]     = useState(null);
  const [err, setErr]       = useState('');
  const [loading, setLoad]  = useState(false);
  const [days, setDays]     = useState(0);      // 0 = tanpa batas tanggal
  const [owner, setOwner]   = useState('');
  const [cli, setCli]       = useState('all');
  const [status, setStatus] = useState('open');   // open | released | all
  const [showPl, setShowPl] = useState(false);    // tampilkan piece list bawaan SAP
  const [openTr, setOpenTr] = useState(null);     // TR yang barisnya sedang dibentangkan
  const [objs, setObjs]     = useState({});       // cache isi TR: { [trkorr]: {loading,data,err} }
  const { sort, onSort, apply } = useSort('age', 'desc');

  // Pilihan default mengikuti daftar dari induk. Direset saat landscape
  // berganti, supaya tidak menyisakan connId milik landscape sebelumnya.
  useEffect(() => {
    if (!conns) return;
    const first = conns.find(c => c.ready) || conns[0];
    setConnId(first ? String(first.id) : '');
    setRows(null); setMeta(null); setOpenTr(null); setObjs({});
  }, [conns]);

  const load = useCallback(async () => {
    if (!connId) return;
    setLoad(true); setErr(''); setRows(null); setMeta(null); setOpenTr(null); setObjs({});
    try {
      const p = new URLSearchParams({ connectionId: connId });
      if (Number(days) > 0) p.set('days', String(days));
      if (owner.trim())     p.set('user', owner.trim());
      p.set('status', status);
      if (showPl) p.set('category', 'all');
      const d = await api(`/api/transport/open?${p.toString()}`);
      setRows(d.items || []);
      setMeta({ system: d.system, truncated: d.truncated, limit: d.limit, raw: d.count, took: d.took_ms });
    } catch (e) {
      setErr(e.message);
      setRows([]);
    }
    setLoad(false);
  }, [api, connId, days, owner, status, showPl]);

  // Isi TR dibaca saat diklik, bukan di muka. Satu TR bisa memuat ribuan objek,
  // dan sebagian besar baris tidak akan pernah dibuka.
  const toggleTr = useCallback(async (tr) => {
    if (openTr === tr) { setOpenTr(null); return; }
    setOpenTr(tr);
    if (objs[tr]?.data || objs[tr]?.loading) return;
    setObjs(o => ({ ...o, [tr]: { loading: true } }));
    try {
      const d = await api(`/api/transport/open/objects?connectionId=${encodeURIComponent(connId)}&trkorr=${encodeURIComponent(tr)}`);
      setObjs(o => ({ ...o, [tr]: { data: d } }));
    } catch (e) {
      setObjs(o => ({ ...o, [tr]: { err: e.message } }));
    }
  }, [api, connId, openTr, objs]);

  const sel = (conns || []).find(c => String(c.id) === String(connId));
  const notReady = sel && !sel.ready;

  // Daftar client yang benar-benar muncul di hasil — dropdown diisi dari data,
  // bukan daftar tetap, supaya tidak menawarkan client yang tidak ada isinya.
  const clientOpts = Array.from(new Set((rows || []).map(r => r.client).filter(Boolean))).sort();

  const filtered = (rows || []).filter(r => {
    if (cli !== 'all') {
      if (cli === 'none' ? r.client : r.client !== cli) return false;
    }
    const s = (q || '').trim().toLowerCase();
    if (!s) return true;
    return (r.trkorr || '').toLowerCase().includes(s)
        || (r.description || '').toLowerCase().includes(s)
        || (r.owner || '').toLowerCase().includes(s);
  });
  const sorted = apply(filtered, (r, col) => ({
    tr: r.trkorr, desc: r.description, type: r.type_label, client: r.client || '',
    owner: r.owner, created: r.created_date || '', age: r.age_days ?? -1,
  }[col]));

  // Umur = sinyal utama. 90+ hari hampir selalu TR yang dilupakan.
  const ageStyle = (d) => {
    if (d == null) return {};
    if (d >= 90) return { color: '#b91c1c', fontWeight: 600 };
    if (d >= 30) return { color: '#b45309', fontWeight: 600 };
    return { color: '#64748b' };
  };
  const stale = sorted.filter(r => (r.age_days ?? 0) >= 90).length;

  return (
    <div>
      {/* Kontrol */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={connId} onChange={e => setConnId(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, background: '#fff' }}>
          {conns === null && <option>{L.loadingConns}</option>}
          {conns !== null && !conns.length && <option value="">{L.noConnProfile}</option>}
          {(conns || []).map(c => (
            <option key={c.id} value={c.id}>{c.label}{c.ready ? '' : L.notReadySuffix}</option>
          ))}
        </select>

        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, background: '#fff' }}>
          <option value="open">{L.statusOpen}</option>
          <option value="released">{L.statusReleased}</option>
          <option value="all">{L.statusAll}</option>
        </select>

        <select value={days} onChange={e => setDays(Number(e.target.value))}
          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, background: '#fff' }}>
          <option value={0}>{L.allDates}</option>
          <option value={30}>{L.last30}</option>
          <option value={90}>{L.last90}</option>
          <option value={365}>{L.last1y}</option>
        </select>

        {clientOpts.length > 0 && (
          <select value={cli} onChange={e => setCli(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, background: '#fff' }}>
            <option value="all">{L.allClients}</option>
            {clientOpts.map(c => <option key={c} value={c}>Client {c}</option>)}
            <option value="none">{L.clientNo}</option>
          </select>
        )}

        <input value={owner} onChange={e => setOwner(e.target.value)} placeholder={L.ownerOpt}
          style={{ width: 150, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13 }} />

        <label title={L.pieceListTip}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#64748b', cursor: 'pointer' }}>
          <input type="checkbox" checked={showPl} onChange={e => setShowPl(e.target.checked)} />
          {L.pieceList}
        </label>

        <button onClick={load} disabled={loading || !connId || notReady}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                   border: '1px solid #16794622', background: (loading || notReady) ? '#f1f5f9' : '#f0fdf4',
                   color: (loading || notReady) ? '#94a3b8' : '#15803d',
                   cursor: (loading || notReady) ? 'not-allowed' : 'pointer', fontSize: 12.5, fontWeight: 600 }}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> {loading ? L.reading : L.load}
        </button>

        {rows !== null && (
          <ExportButton disabled={!sorted.length} onClick={() => exportXlsx({
            columns: [L.thTr, L.thDesc, L.thType, L.thClient, L.thStatus, L.thOwner, L.thCreated, L.exUmur],
            rows: sorted.map(r => [r.trkorr, r.description, r.type_label, r.client || '',
                                   r.status_label, r.owner, r.created_date || '', r.age_days ?? '']),
            sheetName: `Open TR ${meta?.system?.sid || ''}`,
            filename: `Transport_BelumRelease_${meta?.system?.sid || 'DEV'}`,
          })} />
        )}

        {rows !== null && (
          <div style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
            {sorted.length} {L.trCount}
            {meta && sorted.length !== meta.raw && <span> {L.of} {meta.raw} {L.loaded}</span>}
            {stale > 0 && <span style={{ color: '#b91c1c' }}> · {stale} {L.aged90}</span>}
          </div>
        )}
      </div>

      {sel?.mismatch && (
        <div style={{ marginBottom: 12, padding: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#92400e' }}>
          {L.mismatchA} <b>{sel.sid}</b>, {L.mismatchB} <b>{expectedSid}</b>. {L.mismatchC}
        </div>
      )}

      {sel?.host_suspect && (
        <div style={{ marginBottom: 12, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#991b1b' }}>
          {L.hostBad} <code style={{ fontFamily: 'monospace' }}>{sel.host}</code>
        </div>
      )}

      {notReady && (
        <div style={{ marginBottom: 12, padding: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#92400e' }}>
          {L.notReadyA} <b>{sel.label}</b> {L.notReadyB}
        </div>
      )}

      {err && (
        <div style={{ marginBottom: 12, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#991b1b' }}>
          {err}
        </div>
      )}

      {meta?.truncated && (
        <div style={{ marginBottom: 12, padding: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#92400e' }}>
          {L.truncA} {meta.raw} {L.truncB} ({meta.limit}) {L.truncC}
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #f1f5f9', borderRadius: 10, background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <ThSort col="tr"      sort={sort} onSort={onSort} w={130}>{L.thTr}</ThSort>
              <ThSort col="desc"    sort={sort} onSort={onSort}>{L.thDesc}</ThSort>
              <ThSort col="type"    sort={sort} onSort={onSort} w={130}>{L.thType}</ThSort>
              <ThSort col="client"  sort={sort} onSort={onSort} w={80}>{L.thClient}</ThSort>
              <ThSort col="owner"   sort={sort} onSort={onSort} w={120}>{L.thOwner}</ThSort>
              <ThSort col="created" sort={sort} onSort={onSort} w={110}>{L.thCreated}</ThSort>
              <ThSort col="age"     sort={sort} onSort={onSort} w={90}>{L.thAge}</ThSort>
              <Th w={150}>{L.thStatus}</Th>
            </tr>
          </thead>
          <tbody>
            {rows === null && !loading && (
              <tr><Td>—</Td><Td>{L.pickThenLoad}</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td></tr>
            )}
            {loading && (
              <tr><Td>—</Td><Td>{L.readingE070}</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td></tr>
            )}
            {rows !== null && !loading && !sorted.length && (
              <tr><Td>—</Td><Td>{q || cli !== 'all' ? L.noneMatch : (status === 'released' ? L.noneReleased : L.noneOpen)}</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td></tr>
            )}
            {!loading && sorted.map(r => (
              <Fragment key={r.trkorr}>
              <tr onClick={() => toggleTr(r.trkorr)} style={{ cursor: 'pointer' }}
                  title={L.clickForObjects}>
                <Td mono>
                  <span style={{ color: '#0f766e', marginRight: 5, display: 'inline-block',
                                 transform: openTr === r.trkorr ? 'rotate(90deg)' : 'none',
                                 transition: 'transform .15s' }}>›</span>
                  {r.trkorr}
                </Td>
                <Td title={r.description}>{r.description || '—'}</Td>
                <Td title={`TRFUNCTION = ${r.type}`}>{r.type_label}</Td>
                <Td mono title={r.client ? undefined : 'Workbench request — not tied to a client'}>{r.client || '—'}</Td>
                <Td mono>{r.owner}</Td>
                <Td title={r.created_time ? `${r.created_date} ${r.created_time}` : undefined}>{r.created_date || '—'}</Td>
                <Td><span style={ageStyle(r.age_days)}>{r.age_days == null ? '—' : `${r.age_days} ${L.ageUnit}`}</span></Td>
                <Td title={`TRSTATUS = ${r.status}`}>
                  <span style={{ fontSize: 11.5, padding: '2px 8px', borderRadius: 999,
                                 background: r.released ? '#dcfce7' : (r.status === 'L' ? '#fef3c7' : '#f1f5f9'),
                                 color:      r.released ? '#166534' : (r.status === 'L' ? '#92400e' : '#475569') }}>
                    {r.status_label}
                  </span>
                </Td>
              </tr>
              {openTr === r.trkorr && (
                <tr>
                  <td colSpan={8} style={{ padding: 0, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <TrObjects state={objs[r.trkorr]} />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {meta?.system && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#94a3b8' }}>
          {L.source}: {meta.system.sid} {L.clientPrefix} {meta.system.client} ({meta.system.host}) · E070/E07T · {meta.took} ms
          {' · '}{L.crossClient}
          {status === 'released' && L.releasedHint}
          {!showPl && L.plHidden}
        </div>
      )}
    </div>
  );
}

// ── View: Import History penuh per sistem (setara STMS > Goto > Import History) ─
function HistoryView({ ls, q, api, descMap }) {
  const L = useT();
  const [system, setSystem] = useState(ls.qas.sid);
  const [rows, setRows]     = useState(null);
  const [err, setErr]       = useState('');
  const [syncing, setSync]  = useState(false);
  const [lastSync, setLast] = useState(null);
  const { sort, onSort, apply } = useSort('date', 'desc');
  const [showFilter, setShowFilter] = useState(false);
  const emptyF = { dateFrom: '', dateTo: '', rc: 'all', cli: 'all', user: '' };
  const [f, setF] = useState(emptyF);

  useEffect(() => { setSystem(ls.qas.sid); }, [ls]);
  useEffect(() => { setF(emptyF); }, [ls.id, system]); // reset filter saat ganti landscape/sistem

  const mapRow = (h) => {
    const ts = String(pick(h, ['trtime']));
    return {
      tr:   String(pick(h, ['trkorr'])),
      ts,
      date: tsDate(ts),
      time: tsTime(ts),
      step: String(pick(h, ['trstep', 'step'])),
      rc:   parseInt(pick(h, ['retcode', 'rc']), 10),
      user: String(pick(h, ['admin', 'truser', 'user'])),
      cli:  String(pick(h, ['trcli', 'client'])),
      text: String(pick(h, ['as4text', 'text'])),
    };
  };

  const readCache = useCallback(async () => {
    setRows(null); setErr('');
    try {
      const r = await api(`/api/transport/history?landscape=${ls.id}&system=${system}`);
      setRows((Array.isArray(r?.rows) ? r.rows : []).map(mapRow).filter(x => x.tr));
      setLast(r?.lastSync || null);
    } catch (e) { setErr(e.message); setRows([]); }
  }, [ls.id, system, api]);

  useEffect(() => { readCache(); }, [readCache]);

  const doSync = async () => {
    setSync(true); setErr('');
    try {
      const r = await api('/api/transport/history-sync', { method: 'POST', body: JSON.stringify({ landscape: ls.id, system }) });
      await readCache();
      setLast(r?.lastSync || null);
    } catch (e) { setErr(e.message); }
    setSync(false);
  };

  // Opsi dropdown dari data yang ada
  const cliOptions = useMemo(() => [...new Set((rows || []).map(h => h.cli).filter(Boolean))].sort(), [rows]);

  const activeFilterCount = (f.dateFrom ? 1 : 0) + (f.dateTo ? 1 : 0) + (f.rc !== 'all' ? 1 : 0) + (f.cli !== 'all' ? 1 : 0) + (f.user ? 1 : 0);

  const filtered = useMemo(() => {
    const dFrom = f.dateFrom ? f.dateFrom.replace(/-/g, '') : '';      // yyyymmdd
    const dTo   = f.dateTo   ? f.dateTo.replace(/-/g, '')   : '';
    const base = (rows || []).filter(h => {
      // kotak cari (TR/deskripsi/user)
      if (q && q.trim()) {
        const sLow = q.trim().toLowerCase();
        if (!(h.tr.toLowerCase().includes(sLow) || (h.text || descMap.get(h.tr) || '').toLowerCase().includes(sLow) || h.user.toLowerCase().includes(sLow))) return false;
      }
      // rentang tanggal (bandingkan yyyymmdd dari timestamp)
      const day = String(h.ts || '').slice(0, 8);
      if (dFrom && day && day < dFrom) return false;
      if (dTo   && day && day > dTo)   return false;
      // RC
      if (f.rc !== 'all') {
        const rc = h.rc;
        if (f.rc === '0'   && rc !== 0) return false;
        if (f.rc === '4'   && !(rc > 0 && rc <= 4)) return false;
        if (f.rc === '8'   && !(rc >= 8)) return false;
      }
      // client
      if (f.cli !== 'all' && h.cli !== f.cli) return false;
      // owner/user (substring)
      if (f.user && !h.user.toLowerCase().includes(f.user.toLowerCase())) return false;
      return true;
    });
    return apply(base, (h, col) => {
      switch (col) {
        case 'date': return h.ts;          // sort pakai timestamp mentah
        case 'tr':   return h.tr;
        case 'desc': return h.text || descMap.get(h.tr) || '';
        case 'cli':  return h.cli;
        case 'step': return h.step;
        case 'rc':   return isNaN(h.rc) ? -1 : h.rc;
        case 'user': return h.user;
        default:     return '';
      }
    });
  }, [rows, q, descMap, apply]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {[ls.qas.sid, ls.prd.sid].map(sid => (
          <button key={sid} onClick={() => setSystem(sid)}
            style={{ padding: '6px 14px', borderRadius: 10, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
              border: system === sid ? '1px solid rgb(var(--brand-600))' : '1px solid #e2e8f0',
              background: system === sid ? '#f0f9ff' : '#fff', color: system === sid ? 'rgb(var(--brand-600))' : '#475569' }}>
            {sid}
          </button>
        ))}
        {lastSync && <span style={{ fontSize: 11.5, color: '#94a3b8' }}>Cache: {new Date(lastSync).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
        <button onClick={() => setShowFilter(v => !v)}
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 12.5,
            border: activeFilterCount ? '1px solid #0f766e' : '1px solid #e2e8f0',
            background: activeFilterCount ? '#f0fdfa' : '#fff', color: activeFilterCount ? '#0f766e' : '#475569' }}>
          <SlidersHorizontal size={13} /> Filter{activeFilterCount ? ` (${activeFilterCount})` : ''}
        </button>
        <button onClick={doSync} disabled={syncing}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12.5 }}>
          <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} /> {syncing ? L.syncing : L.refreshHistory}
        </button>
        <ExportButton disabled={!filtered.length} onClick={() => exportXlsx({
          sheetName: `History ${ls.id} ${system}`,
          filename: `Transport_History_${ls.id}_${system}`,
          columns: [
            { key: 'date', label: 'Tanggal' }, { key: 'time', label: 'Jam' },
            { key: 'tr', label: L.thTr }, { key: 'desc', label: L.thDesc },
            { key: 'cli', label: 'Client' }, { key: 'step', label: 'Step' },
            { key: 'rc', label: 'RC' }, { key: 'user', label: 'User' },
          ],
          rows: filtered.map(h => ({ date: h.date, time: h.time, tr: h.tr, desc: h.text || descMap.get(h.tr) || '', cli: h.cli, step: h.step, rc: isNaN(h.rc) ? '' : h.rc, user: h.user })),
        })} />
        <div style={{ fontSize: 12, color: '#94a3b8' }}>{rows ? `${filtered.length} entri` : ''}</div>
      </div>

      {showFilter && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10, padding: 12, border: '1px solid #e2e8f0', borderRadius: 10, background: '#f8fafc' }}>
          <label style={{ fontSize: 12, color: '#475569' }}>Tanggal dari<br/>
            <input type="date" value={f.dateFrom} onChange={e => setF({ ...f, dateFrom: e.target.value })}
              style={{ marginTop: 4, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }} />
          </label>
          <label style={{ fontSize: 12, color: '#475569' }}>sampai<br/>
            <input type="date" value={f.dateTo} onChange={e => setF({ ...f, dateTo: e.target.value })}
              style={{ marginTop: 4, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }} />
          </label>
          <label style={{ fontSize: 12, color: '#475569' }}>RC<br/>
            <select value={f.rc} onChange={e => setF({ ...f, rc: e.target.value })}
              style={{ marginTop: 4, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}>
              <option value="all">Semua</option>
              <option value="0">RC 0 (sukses)</option>
              <option value="4">RC 1–4 (warning)</option>
              <option value="8">RC ≥ 8 (error)</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#475569' }}>Client<br/>
            <select value={f.cli} onChange={e => setF({ ...f, cli: e.target.value })}
              style={{ marginTop: 4, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, minWidth: 80 }}>
              <option value="all">Semua</option>
              {cliOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#475569' }}>{L.ownerUser}<br/>
            <input value={f.user} onChange={e => setF({ ...f, user: e.target.value })} placeholder="mis. PGC_ABAP"
              style={{ marginTop: 4, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }} />
          </label>
          {activeFilterCount > 0 && (
            <button onClick={() => setF(emptyF)}
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12.5, color: '#dc2626' }}>
              <X size={12} style={{ verticalAlign: -2, marginRight: 3 }} />Reset
            </button>
          )}
        </div>
      )}
      {err && <div style={{ marginBottom: 10, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#991b1b' }}>{err}</div>}
      <div style={{ overflowX: 'auto', border: '1px solid #f1f5f9', borderRadius: 10, background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <ThSort w={95} col="date" sort={sort} onSort={onSort}>Tanggal</ThSort>
              <Th w={70}>Jam</Th>
              <ThSort w={130} col="tr" sort={sort} onSort={onSort}>TR</ThSort>
              <ThSort col="desc" sort={sort} onSort={onSort}>{L.thDesc}</ThSort>
              <ThSort w={60} col="cli" sort={sort} onSort={onSort}>Client</ThSort>
              <ThSort w={70} col="step" sort={sort} onSort={onSort}>Step</ThSort>
              <ThSort w={70} col="rc" sort={sort} onSort={onSort}>RC</ThSort>
              <ThSort w={100} col="user" sort={sort} onSort={onSort}>User</ThSort>
            </tr>
          </thead>
          <tbody>
            {filtered.map((h, i) => (
              <tr key={i}>
                <Td>{h.date}</Td>
                <Td>{h.time}</Td>
                <Td mono title={h.tr}>{h.tr}</Td>
                <Td title={h.text || descMap.get(h.tr) || ''}>{h.text || descMap.get(h.tr) || '—'}</Td>
                <Td mono>{h.cli || '—'}</Td>
                <Td mono>{h.step || '—'}</Td>
                <Td><Badge text={isNaN(h.rc) ? '—' : `RC ${h.rc}`} color={isNaN(h.rc) ? '#64748b' : h.rc === 0 ? '#16a34a' : h.rc <= 4 ? '#d97706' : '#dc2626'} /></Td>
                <Td>{h.user || '—'}</Td>
              </tr>
            ))}
            {rows !== null && !filtered.length && !err && (
              <tr><Td>—</Td><Td>—</Td><Td>—</Td><Td>{lastSync ? L.histNoMatch : L.histEmpty}</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td></tr>
            )}
          </tbody>
        </table>
      </div>
      {rows === null && !err && <div style={{ marginTop: 10, fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={13} /> Memuat history {system}…</div>}
    </div>
  );
}

// ── Panel detail TR (objek + riwayat QAS/PRD landscape aktif) ─────────────────
function DetailPanel({ tr, ls, api, onClose }) {
  const L = useT();
  const [data, setData] = useState(null);
  const [err, setErr]   = useState('');
  useEffect(() => {
    let ok = true;
    api(`/api/transport/detail?landscape=${ls.id}&tr=${encodeURIComponent(tr)}`)
      .then(r => { if (ok) setData(r.data); })
      .catch(e => { if (ok) setErr(e.message); });
    return () => { ok = false; };
  }, [tr, ls.id, api]);

  const objects = useMemo(() => {
    const d = data || {};
    const list = d.OBJECTS || d.objects || [];
    return Array.isArray(list) ? list : [];
  }, [data]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 20, width: 680, maxWidth: '100%', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Eye size={16} color="rgb(var(--brand-600))" />
          <div style={{ fontWeight: 700, fontFamily: 'monospace' }}>{tr}</div>
          <Badge text={`${ls.label} · ${ls.route}`} color="rgb(var(--brand-600))" />
          <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        {err && <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 10 }}>{err}</div>}
        {data && <div style={{ fontSize: 13, marginBottom: 12 }}><b>{L.descLabel}</b> {String(data.TEXT || data.text || '—')}</div>}

        <HistoryBlock tr={tr} landscape={ls.id} system={ls.qas.sid} api={api} />
        <HistoryBlock tr={tr} landscape={ls.id} system={ls.prd.sid} api={api} />

        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>Objek ({objects.length})</div>
        {!err && !data && <div style={{ fontSize: 12.5, color: '#64748b' }}>{L.loading}</div>}
        {data && (
          <div style={{ border: '1px solid #f1f5f9', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><Th>PGMID</Th><Th>Type</Th><Th>Object</Th></tr></thead>
              <tbody>
                {objects.map((o, i) => (
                  <tr key={i}>
                    <Td mono>{String(pick(o, ['pgmid']))}</Td>
                    <Td mono>{String(pick(o, ['object']))}</Td>
                    <Td mono title={String(pick(o, ['obj_name']))}>{String(pick(o, ['obj_name']))}</Td>
                  </tr>
                ))}
                {!objects.length && <tr><Td>—</Td><Td>—</Td><Td>{L.noObjectData}</Td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Halaman utama ─────────────────────────────────────────────────────────────
export default function TransportRequest() {
  const L = useT();
  const api      = useApi();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const canImport = user?.role === 'admin' || (user?.permissions || []).includes('transport_import');
  const sortMerged = useSort('bufpos', 'desc');   // tabel Lintas Landscape
  const sortRows   = useSort('bufpos', 'desc');   // tabel per-sistem

  const [searchParams] = useSearchParams();
  const initialTr = parseTrTab(searchParams.get('tab'));
  const [lsId, setLsId]     = useState(initialTr?.lsId || LANDSCAPES[0].id);
  const [tab, setTab]       = useState(initialTr ? initialTr.tab : LANDSCAPES[0].qas.sid);  // <qas sid> | <prd sid> | HIST | OPEN
  // Setter khusus: memilih SID lewat deep link harus menggeser landscape aktif
  // juga, kalau tidak `tab` menunjuk sistem yang tidak ada di landscape yang
  // sedang tampil dan panelnya kosong tanpa pesan error.
  useTabDeepLink('transport', (p) => !!parseTrTab(p), (p) => {
    const d = parseTrTab(p);
    if (!d) return;
    if (d.lsId) setLsId(d.lsId);
    setTab(d.tab);
  });
  const [rows, setRows]     = useState([]);             // mode per-sistem
  const [merged, setMerged] = useState([]);             // mode lintas landscape
  const [loading, setLoad]  = useState(false);
  const [syncing, setSync]  = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [err, setErr]       = useState('');
  const [q, setQ]           = useState('');
  const [onlyWaiting, setOnlyWaiting] = useState(false);
  const [importReq, setImportReq]     = useState(null);  // { row, system, clients, isPrd }
  const [detailTr, setDetailTr]       = useState(null);
  const [toast, setToast]             = useState(null);

  const ls = LANDSCAPES.find(l => l.id === lsId);

  // Ambil entri TERAKHIR (bufpos tertinggi) per TR — satu TR bisa diimport berulang.
  const latestPerTr = (list) => {
    const m = new Map();
    for (const r of list) {
      const prev = m.get(r.tr);
      if (!prev || Number(r.bufpos || 0) > Number(prev.bufpos || 0)) m.set(r.tr, r);
    }
    return m;
  };

  // Baca CACHE dari backend (instan). rows dari tabel sql.js.
  const readCache = useCallback(async (lsObj, which) => {
    const parse = (r) => (Array.isArray(r?.rows) ? r.rows : []).map(normRow).filter(x => x.tr);
    if (which === TAB_ALL) {
      const [rq, rp] = await Promise.all([
        api(`/api/transport/queue?landscape=${lsObj.id}&system=${lsObj.qas.sid}`),
        api(`/api/transport/queue?landscape=${lsObj.id}&system=${lsObj.prd.sid}`),
      ]);
      const mq = latestPerTr(parse(rq));
      const mp = latestPerTr(parse(rp));
      const all = new Map();
      for (const [trk, r] of mq) all.set(trk, { tr: trk, text: r.text, owner: r.owner, bufpos: r.bufpos, qas: r, prd: null });
      for (const [trk, r] of mp) {
        const e = all.get(trk);
        if (e) { e.prd = r; if (!e.text) e.text = r.text; }
        else   all.set(trk, { tr: trk, text: r.text, owner: r.owner, bufpos: r.bufpos, qas: null, prd: r });
      }
      setMerged([...all.values()].sort((a, b) => Number(b.bufpos || 0) - Number(a.bufpos || 0)));
      const syncs = [rq?.lastSync, rp?.lastSync].filter(Boolean).sort();
      setLastSync(syncs[0] || null); // tampilkan sync TERTUA dari kedua sistem
      return parse(rq).length + parse(rp).length;
    }
    const r = await api(`/api/transport/queue?landscape=${lsObj.id}&system=${which}`);
    setRows(parse(r).sort((a, b) => Number(b.bufpos || 0) - Number(a.bufpos || 0))); // terbaru di atas
    setLastSync(r?.lastSync || null);
    return parse(r).length;
  }, [api]);

  // Sync delta ke SAP untuk sistem yang relevan pada view aktif, lalu re-baca cache.
  const syncNow = useCallback(async (lsObj, which) => {
    setSync(true); setErr('');
    try {
      const systems = which === TAB_ALL ? [lsObj.qas.sid, lsObj.prd.sid] : [which];
      const results = await Promise.all(systems.map(sid =>
        api('/api/transport/sync', { method: 'POST', body: JSON.stringify({ landscape: lsObj.id, system: sid }) })
      ));
      await readCache(lsObj, which);
      const delta = results.reduce((a, r) => ({ added: a.added + (r.added || 0), updated: a.updated + (r.updated || 0), removed: a.removed + (r.removed || 0) }), { added: 0, updated: 0, removed: 0 });
      if (delta.added || delta.updated || delta.removed) {
        setToast({ msg: `Sync: ${delta.added} baru, ${delta.updated} berubah, ${delta.removed} dihapus.` });
        setTimeout(() => setToast(null), 5000);
      }
    } catch (e) { setErr(e.message); }
    setSync(false);
  }, [api, readCache]);

  const load = useCallback(async (lsObj, which) => {
    if (which === TAB_HIST || which === TAB_OPEN || which === TAB_TRST) { setErr(''); return; } // view ini memuat datanya sendiri
    setLoad(true); setErr('');
    setRows([]); setMerged([]); setLastSync(null); // bersihkan data landscape/tab sebelumnya
    try {
      const n = await readCache(lsObj, which);
      if (n === 0) { setLoad(false); await syncNow(lsObj, which); return; } // cache kosong → sync pertama otomatis
    } catch (e) { setErr(e.message); setRows([]); setMerged([]); }
    setLoad(false);
  }, [readCache, syncNow]);

  useEffect(() => { load(ls, tab); }, [ls, tab, load]);

  // Ganti landscape → reset sub-tab & filter
  const switchLandscape = (id) => { setLsId(id); setTab(LANDSCAPES.find(l => l.id === id).qas.sid); setQ(''); setOnlyWaiting(false); };

  const match = useCallback((r) => {
    const s = (q || '').trim().toLowerCase();
    if (!s) return true;
    return r.tr.toLowerCase().includes(s) || (r.text || '').toLowerCase().includes(s) || (r.owner || '').toLowerCase().includes(s);
  }, [q]);

  const filtered = useMemo(() => {
    const base = rows.filter(r => {
      if (onlyWaiting && !isWaiting(r)) return false;
      return match(r);
    });
    return sortRows.apply(base, (r, col) => {
      switch (col) {
        case 'tr':     return r.tr;
        case 'desc':   return r.text || '';
        case 'owner':  return r.owner || '';
        case 'bufpos': return Number(r.bufpos || 0);
        case 'status': return isWaiting(r) ? -1 : (isNaN(parseInt(r.rc, 10)) ? -2 : parseInt(r.rc, 10));
        default:       return '';
      }
    });
  }, [rows, match, onlyWaiting, sortRows]);

  const rcSortVal = (x) => !x ? -3 : isWaiting(x) ? -1 : (isNaN(parseInt(x.rc, 10)) ? -2 : parseInt(x.rc, 10));
  const filteredMerged = useMemo(() => {
    const base = merged.filter(r => {
      if (onlyWaiting && !(isWaiting(r.qas) || isWaiting(r.prd) || !r.prd)) return false;
      return match(r);
    });
    return sortMerged.apply(base, (r, col) => {
      switch (col) {
        case 'tr':     return r.tr;
        case 'desc':   return r.text || '';
        case 'owner':  return r.owner || '';
        case 'bufpos': return Number(r.bufpos || 0);
        case 'qas':    return rcSortVal(r.qas);
        case 'prd':    return rcSortVal(r.prd);
        default:       return '';
      }
    });
  }, [merged, match, onlyWaiting, sortMerged]);

  // Peta TR → deskripsi dari cache queue (untuk kolom deskripsi di History)
  const descMap = useMemo(() => {
    const m = new Map();
    for (const r of merged) if (r.text) m.set(r.tr, r.text);
    for (const r of rows)   if (r.text && !m.has(r.tr)) m.set(r.tr, r.text);
    return m;
  }, [merged, rows]);

  const sysCfgOf = (sid) => (sid === ls.qas.sid ? ls.qas : ls.prd);
  const isPrdSid = (sid) => sid === ls.prd.sid;

  const onImported = (r) => {
    setImportReq(null);
    const okAll = r.results.every(x => x.ok);
    setToast({ msg: `Import ${r.tr} → ${r.system} (${r.results.map(x => x.client).join(', ')}) ${okAll ? 'dipicu' : 'sebagian gagal'}. Refresh queue untuk melihat RC.` });
    setTimeout(() => setToast(null), 7000);
    syncNow(ls, tab);
  };

  const count = tab === TAB_ALL ? filteredMerged.length : filtered.length;
  const isHist = tab === TAB_HIST;
  const isOpen = tab === TAB_OPEN;
  const isTrSt = tab === TAB_TRST;
  // TAB_TRST WAJIB ikut dikecualikan di sini. isQueue mengendalikan bar filter
  // antrean, tombol Export Excel dan tombol Refresh/sync STMS — semuanya milik
  // antrean live dan tidak berlaku untuk snapshot config. Kalau tab ini terhitung
  // "queue", Refresh akan memanggil sync STMS yang tidak ada hubungannya dengan
  // data yang sedang tampil, dan Export akan mengekspor baris antrean, bukan
  // baris yang dilihat pengguna.
  const isQueue = !isHist && !isOpen && !isTrSt;   // tab antrean STMS (Q/P/ALL)

  // Koneksi Dev untuk landscape yang sedang aktif. Dipakai dua tempat:
  // tab Belum Release (pemilih koneksi) dan tab antrean (baca isi TR).
  const devConns = useDevConns(api, ls.id, ls.label);
  // Objek TR selalu dibaca dari sistem DEV tempat TR dibuat, bukan dari sistem
  // target — TR di antrean Q01 pun objeknya tersimpan di D01.
  const devConnId = (devConns || []).find(c => c.ready)?.id || null;
  const trObj = useTrObjects(api, devConnId);

  const tabBtn = (active) => ({
    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: active ? '1px solid #0f766e' : '1px solid #e2e8f0',
    background: active ? '#f0fdfa' : '#fff', color: active ? '#0f766e' : '#475569',
  });

  return (
    <div style={{ padding: isMobile ? 12 : 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Truck size={20} color="#0f766e" />
        <div>
          <div style={{ fontWeight: 700, fontSize: 17, color: '#0f172a' }}>SAP Transport Request</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>STMS import queue — landscape {ls.label} ({ls.route})</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastSync && isQueue && (
            <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
              Cache: {new Date(lastSync).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {isQueue && (
            <ExportButton disabled={count === 0} onClick={() => {
              if (tab === TAB_ALL) {
                return exportXlsx({
                  sheetName: `Landscape ${ls.id}`,
                  filename: `Transport_${ls.id}_Lintas`,
                  columns: [
                    { key: 'tr', label: L.thTr }, { key: 'desc', label: L.thDesc }, { key: 'owner', label: L.thOwner },
                    { key: 'qas', label: ls.qas.sid }, { key: 'prd', label: ls.prd.sid },
                  ],
                  rows: filteredMerged.map(r => ({ tr: r.tr, desc: r.text || '', owner: r.owner || '', qas: rcLabel(r.qas), prd: rcLabel(r.prd) })),
                });
              }
              return exportXlsx({
                sheetName: `${ls.id} ${tab}`,
                filename: `Transport_${ls.id}_${tab}`,
                columns: [
                  { key: 'tr', label: L.thTr }, { key: 'desc', label: L.thDesc }, { key: 'owner', label: L.thOwner },
                  { key: 'pos', label: 'Pos' }, { key: 'status', label: 'Status' },
                ],
                rows: filtered.map(r => ({ tr: r.tr, desc: r.text || '', owner: r.owner || '', pos: r.bufpos.replace(/^0+/, '') || '', status: rcLabel(r) })),
              });
            }} />
          )}
          {/* Tombol sync STMS. Di tab TR status ia DISEMBUNYIKAN, bukan sekadar
              dinonaktifkan: tombol Refresh yang terlihat di atas tabel bersnapshot
              mingguan menjanjikan pemuatan ulang yang tidak akan pernah terjadi.
              Data tab itu berasal dari cron, bukan dari tombol ini. */}
          {!isTrSt && (
          <button onClick={() => syncNow(ls, tab)} disabled={loading || syncing || !isQueue}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
            <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} /> {syncing ? L.syncing : L.refresh}
          </button>
          )}
        </div>
      </div>

      {/* Tab level 1: landscape */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        {LANDSCAPES.map(l => (
          <button key={l.id} onClick={() => switchLandscape(l.id)} style={{ ...tabBtn(lsId === l.id), borderWidth: 2 }}>
            {l.label}
          </button>
        ))}
      </div>

      {/* Tab level 2: view dalam landscape */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => setTab(TAB_TRST)} style={{ ...tabBtn(tab === TAB_TRST), display: 'flex', alignItems: 'center', gap: 6 }}>
          <ListChecks size={13} /> {L.trStatus}
        </button>
        <button onClick={() => setTab(ls.qas.sid)} style={tabBtn(tab === ls.qas.sid)}>{ls.qas.sid} — {L.quality}</button>
        <button onClick={() => setTab(ls.prd.sid)} style={tabBtn(tab === ls.prd.sid)}>{ls.prd.sid} — {L.production}</button>
        <button onClick={() => setTab(TAB_HIST)} style={{ ...tabBtn(tab === TAB_HIST), display: 'flex', alignItems: 'center', gap: 6 }}>
          <History size={13} /> {L.history}
        </button>
        <button onClick={() => setTab(TAB_OPEN)} style={{ ...tabBtn(tab === TAB_OPEN), display: 'flex', alignItems: 'center', gap: 6 }}>
          <Pencil size={13} /> {L.notReleased}
        </button>
      </div>

      {/* Filter antrean STMS.
          Disembunyikan di tab TR status: tab itu punya bar filternya SENDIRI
          (pencarian, module, tipe, status, dropdown tersaring) yang menembak
          endpoint berbeda. Membiarkan keduanya tampil memberi DUA kotak
          pencarian bersebelahan yang menyaring dua himpunan berbeda — yang atas
          tidak akan berpengaruh apa pun terhadap tabel di bawahnya. */}
      {!isTrSt && (
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={L.search}
          style={{ flex: '1 1 220px', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13 }} />
        {isQueue && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyWaiting} onChange={e => setOnlyWaiting(e.target.checked)} />
            {tab === TAB_ALL ? `${L.onlyNotIn} ${ls.prd.sid}` : L.onlyWaiting}
          </label>
        )}
        {isQueue && <div style={{ fontSize: 12, color: '#94a3b8' }}>{count} {L.trCount}</div>}
      </div>
      )}

      {err && isQueue && <div style={{ marginBottom: 12, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#991b1b' }}>{err}</div>}

      {isHist && <HistoryView ls={ls} q={q} api={api} descMap={descMap} />}

      {isOpen && <OpenTRView q={q} api={api} conns={devConns} expectedSid={ls.id} />}

      {isTrSt && <TrStatusView api={api} connId={devConnId} lsLabel={ls.label} />}

      {/* Tabel */}
      {isQueue && (
      <div style={{ overflowX: 'auto', border: '1px solid #f1f5f9', borderRadius: 10, background: '#fff' }}>
        {tab === TAB_ALL ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ThSort w={130} col="tr" sort={sortMerged.sort} onSort={sortMerged.onSort}>{L.thTr}</ThSort>
                <ThSort col="desc" sort={sortMerged.sort} onSort={sortMerged.onSort}>{L.thDesc}</ThSort>
                <ThSort w={100} col="owner" sort={sortMerged.sort} onSort={sortMerged.onSort}>{L.thOwner}</ThSort>
                <ThSort w={110} col="qas" sort={sortMerged.sort} onSort={sortMerged.onSort}>{ls.qas.sid}</ThSort>
                <ThSort w={110} col="prd" sort={sortMerged.sort} onSort={sortMerged.onSort}>{ls.prd.sid}</ThSort>
                <Th w={canImport ? 180 : 80}>Aksi</Th>
              </tr>
            </thead>
            <tbody>
              {filteredMerged.map((r) => (
                <Fragment key={r.tr}>
                <tr>
                  <Td mono title={r.tr}>
                    <TrCell tr={r.tr} open={trObj.openTr === r.tr} onClick={trObj.toggle} />
                  </Td>
                  <Td title={r.text}>{r.text || '—'}</Td>
                  <Td>{r.owner || '—'}</Td>
                  <Td><Badge text={rcLabel(r.qas)} color={rcColor(r.qas)} /></Td>
                  <Td><Badge text={rcLabel(r.prd)} color={rcColor(r.prd)} /></Td>
                  <Td>
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <button onClick={() => setDetailTr(r.tr)} title={L.detailTitle}
                        style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12 }}>
                        <Eye size={13} style={{ verticalAlign: -2 }} />
                      </button>
                      {canImport && isWaiting(r.qas) && (
                        <button onClick={() => setImportReq({ row: { tr: r.tr, text: r.text }, system: ls.qas.sid, clients: ls.qas.clients, isPrd: false })}
                          style={{ padding: '4px 10px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          <Upload size={12} style={{ verticalAlign: -2, marginRight: 3 }} />{ls.qas.sid}
                        </button>
                      )}
                      {canImport && r.prd && isWaiting(r.prd) && (
                        <button onClick={() => setImportReq({ row: { tr: r.tr, text: r.text }, system: ls.prd.sid, clients: ls.prd.clients, isPrd: true })}
                          style={{ padding: '4px 10px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          <Upload size={12} style={{ verticalAlign: -2, marginRight: 3 }} />{ls.prd.sid}
                        </button>
                      )}
                    </span>
                  </Td>
                </tr>
                {trObj.openTr === r.tr && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <TrObjects state={trObj.cache[r.tr]} noConn={!devConnId} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
              {!loading && !filteredMerged.length && (
                <tr><Td>—</Td><Td>{err ? L.queueFail : L.noMatch}</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ThSort w={130} col="tr" sort={sortRows.sort} onSort={sortRows.onSort}>{L.thTr}</ThSort>
                <ThSort col="desc" sort={sortRows.sort} onSort={sortRows.onSort}>{L.thDesc}</ThSort>
                <ThSort w={100} col="owner" sort={sortRows.sort} onSort={sortRows.onSort}>{L.thOwner}</ThSort>
                <ThSort w={70} col="bufpos" sort={sortRows.sort} onSort={sortRows.onSort}>{L.thPos}</ThSort>
                <ThSort w={80} col="status" sort={sortRows.sort} onSort={sortRows.onSort}>{L.thStatus}</ThSort>
                <Th w={canImport ? 160 : 80}>{L.thAction}</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <Fragment key={r.tr + '_' + r.bufpos}>
                <tr>
                  <Td mono title={r.tr}>
                    <TrCell tr={r.tr} open={trObj.openTr === r.tr} onClick={trObj.toggle} />
                  </Td>
                  <Td title={r.text}>{r.text || '—'}</Td>
                  <Td>{r.owner || '—'}</Td>
                  <Td mono>{r.bufpos.replace(/^0+/, '') || '—'}</Td>
                  <Td><Badge text={rcLabel(r)} color={rcColor(r)} /></Td>
                  <Td>
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <button onClick={() => setDetailTr(r.tr)} title={L.detailTitle}
                        style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12 }}>
                        <Eye size={13} style={{ verticalAlign: -2 }} />
                      </button>
                      {canImport && (
                        <button onClick={() => setImportReq({ row: r, system: tab, clients: sysCfgOf(tab).clients, isPrd: isPrdSid(tab), alreadyImported: !isWaiting(r) })}
                          style={{ padding: '4px 10px', borderRadius: 8, border: 'none', background: isPrdSid(tab) ? '#dc2626' : '#0f766e', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          <Upload size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Import
                        </button>
                      )}
                    </span>
                  </Td>
                </tr>
                {trObj.openTr === r.tr && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <TrObjects state={trObj.cache[r.tr]} noConn={!devConnId} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
              {!loading && !filtered.length && (
                <tr><Td>—</Td><Td>{err ? L.queueFail : L.queueEmpty}</Td><Td>—</Td><Td>—</Td><Td>—</Td><Td>—</Td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      )}

      {(loading || syncing) && <div style={{ marginTop: 10, fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={13} /> {syncing ? L.syncWithSap : L.loadingCache} {ls.id}{tab === TAB_ALL ? ` (${ls.qas.sid} + ${ls.prd.sid})` : ` ${tab}`}…</div>}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#0f172a', color: '#fff', padding: '12px 16px', borderRadius: 12, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', zIndex: 70, maxWidth: 420 }}>
          <CheckCircle2 size={15} color="#4ade80" /> {toast.msg}
        </div>
      )}

      {importReq && (
        <ImportDialog row={importReq.row} landscape={ls.id} system={importReq.system}
          clients={importReq.clients} isPrd={importReq.isPrd} alreadyImported={importReq.alreadyImported}
          api={api} onClose={() => setImportReq(null)} onDone={onImported} />
      )}
      {detailTr && <DetailPanel tr={detailTr} ls={ls} api={api} onClose={() => setDetailTr(null)} />}
    </div>
  );
}
